import { Injectable, OnDestroy } from "@angular/core";
import {GridApi, GridOptions } from "@ag-grid-community/core";
import { ReplaySubject, Subject, takeUntil } from "rxjs";
import { ChannelsCellRendererComponent } from "../ag-grid-components/channels-cell-renderer/channels-cell-renderer.component";
import { LoadingCellRendererComponent } from "../ag-grid-components/loading-cell-renderer/loading-cell-renderer.component";
import { OrderInfoCellRendererComponent } from "./../ag-grid-components/order-info-cell-renderer/order-info-cell-renderer.component";
import { OrderStatusCellRendererComponent } from "../ag-grid-components/order-status-cell-renderer/order-status-cell-renderer.component";
import { SkuCellRendererComponent } from "../ag-grid-components/sku-cell-renderer/sku-cell-renderer.component";
import { StatusBarRendererComponent } from "../ag-grid-components/status-bar-renderer/status-bar-renderer.component";
import { CustomHeaderTooltipComponent } from "../ag-grid-components/custom-header-tooltip/custom-header-tooltip.component";
import { GridActionsService } from "./grid-actions.service";
import { GridApiService } from "./grid-api.service";
import { CheckboxCellRendererComponent } from "../ag-grid-components/checkbox-cell-renderer/checkbox-cell-renderer.component";
import { AvalableDateLabels, DatePickerModel } from "../components/date-picker-dialog/date-picker.model";
import { SkuToolPanelComponent } from "../ag-grid-components/sku-tool-panel/sku-tool-panel.component";
import { MultiSkuToolPanelComponent } from "../ag-grid-components/multi-sku-tool-panel/multi-sku-tool-panel.component";
import { PlanyardService } from "../modules/planyard/planyard.service";
import { BooleanFilterComponent } from "../ag-grid-components/boolean-filter/boolean-filter.component";
import { GridReadyEvent } from "@ag-grid-community/core";


@Injectable({
  providedIn: "root",
})
export class OrdersGridConfigService implements OnDestroy {
  gridApi: GridApi;
  accountFilterList$: ReplaySubject<[string]> = new ReplaySubject();
  firstRender: boolean;
  isGridReady: boolean = false;
  dateFilter: DatePickerModel = {
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date(),
    label: AvalableDateLabels.Last30Days,
  };
  allAccounts:any[] = [];
  dialogRef:any;
  spin:boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private gridActionsService: GridActionsService,
    private gridApiService: GridApiService,
    private gridActionService : GridActionsService
  ) {
    this.gridApiService.gridApi$.pipe(takeUntil(this.destroy$)).subscribe((e: GridReadyEvent) => {
      this.gridApi = e.api;
      this.gridApi.closeToolPanel();
      this.gridApi.deselectAll();
    });
  }

  orderColumnDefs=[
    {
      headerName: "Order Id",
      headerTooltip: "Order Id",
      backendFields: ["Orderid", "Purchased"],
      cellRenderer: "orderInfoCellRendererComponent",
      field: "Orderid",
      filter: "agMultiColumnFilter",
      filterParams: {
       filters: [
         {
           filter: "agTextColumnFilter",
           display: "accordion",
           title: "Order Id",
           filterParams: {
             defaultOption: "startsWith",
             maxNumConditions : 1,
           },
         },
         {
           filter: "agDateColumnFilter",
           display: "accordion",
           title: "Order Date",
           filterParams: {
             defaultOption: "inRange",
             maxNumConditions : 1,
           },
         },
       ],
     },
      width: 240,
    },
    {
      field: "Sku",
      backendFields: [
        "Image",
        "Sku",
        "Asin",
        "Title",
        "AccountType",
        "FulfillmentType",
      ],
      headerName: "Product details",
      headerTooltip: "Product details",
      minWidth: 440,

      cellRenderer: "skuCellRendererComponent",
      filter: "agMultiColumnFilter",
      filterParams: {

        filters: [
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "SKU",
            filterParams: {

              defaultOption: "startsWith",

              maxNumConditions : 1,
            },
          },
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "ASIN",
            filterParams: {

              defaultOption: "startsWith",

              maxNumConditions : 1,
            },
          },
          {
            display: "accordion",
            title: "Title",
            filter: "agTextColumnFilter",
            filterParams: {

              defaultOption: "startsWith",

              maxNumConditions : 1,
            },
          },
        ],

      },  

    },
    {
      field: "Qty",
      backendFields: ["Qty"],
      headerName: "QTY",
      headerTooltip: "QTY",

      minWidth: 70,
      filter: "agNumberColumnFilter",

    },
    {
      field: "Price",
      headerName: "Total Price",
      backendFields: ["Price"],
      headerTooltip: "Total Price",

      width: 120,
      filter: "agNumberColumnFilter",
      valueFormatter: (param) =>
        param.value ? "$" + param.value.toFixed(2) : "", 
    },
    {
      field: "Status",
      headerTooltip: "Status",
      backendFields: ["Status"],
      cellRenderer: "orderStatusCellRendererComponent",
      width: 180,

      filter: "agSetColumnFilter",
      filterParams: {
        values: ["Pending", "Shipped", "Cancelled"],
      },        

    },
    {
      field: "Channel",
      headerTooltip: "Sales Channel",
      backendFields: ["Channel"],
      cellRenderer: "channelsCellRendererComponent",
      headerName: "Sales Channel",
      filter: "agTextColumnFilter",
      cellRendererParams: (params) => {
        return {
          channels: [params.data.Channel],
          large :true
        };
      },
      width: 120,
    },
    {
      field: "FulfillmentType",
      backendFields: ["FulfillmentType"],
      headerName: "Fulfillment Type",
      headerTooltip: "Fulfillment Type",
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["FBA", "FBM"],
      },
      width: 100,
    },
    {
      field: "Isbsns",
      headerName: "Business",
      headerTooltip: "Business",
      cellRenderer: "checkboxCellRendererComponent",
      width:100
    },
    {
      field: "ShipCost",
      headerName: "Ship Cost",
      backendFields: ["ShipCost"],
      headerTooltip:"Ship Cost",
      width: 120,
      valueFormatter: (param) =>
        param.value ? "$" + param.value.toFixed(2) : "",
      filter: "agNumberColumnFilter",
      filterParams: {
        defaultOption: "inRange",
        maxNumConditions : 1,
      },
    },
  ]

  columnDefs :any= [
    {
      headerName: "Order Info",
      headerTooltip:"Order Info",
      backendFields: ["Orderid","Purchased"],
      cellRenderer: "orderInfoCellRendererComponent",
      field: "Purchased",
      sortable:true,

      required: true,

     

      width: 220,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "Order ID",
            filterParams: {
              defaultOption: "startsWith",
              maxNumConditions : 1,
            },
          },
          {
            filter: "agDateColumnFilter",
            display: "accordion",
            title: "Order Date",
            filterParams: {
              defaultOption: "inRange",
              maxNumConditions : 1,
            },
          },
        ],
      },
    },
    {
      field: "Sku",
      backendFields: [
        "Image",
        "Sku",
        "Asin",
        "Title",
        "AccountType",
        "AccountSkuId"
      ],
      headerName: "Product",
      headerTooltip:"Product",
      sortable: true,
      minWidth: 440,
      required:true,
      cellRenderer: "skuCellRendererComponent",
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "SKU",
            filterParams: {
              defaultOption: "startsWith",
              maxNumConditions : 1,
            },
          },
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "ASIN",
            filterParams: {
              defaultOption: "startsWith",
              maxNumConditions : 1,
            },
          },
          {
            display: "accordion",
            title: "Description",
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "startsWith",
              maxNumConditions : 1,
            },
          },
        ],
      },
    },
    {
      field: "Qty",
      headerTooltip:"Qty",
      width: 120,
      backendFields:['Qty'],
      sortable:true,
      filter: "agNumberColumnFilter",
      cellEditor: "numericCellEditor",
      valueGetter: (params) => {
        return (Number(params.data.Qty) || 0).toLocaleString('en');
      },
      editable: (params:any)=> params.data.Account == 'My Channel' ? true : false,
      onCellValueChanged: (event) =>
      this.gridActionService.UpdateOrderQtyPrice(event,"Qty"),       
    },
    {
      field: "Price",
      headerName: "My Price",
      backendFields:['Price'],
      headerTooltip:"My Price",
      width: 120,
      sortable:true,
      valueFormatter: (param) =>
        param.value ? "$" + (parseFloat(Number(param.value).toFixed(2))).toLocaleString('en') : "",
      filter: "agNumberColumnFilter",
      filterParams: {
        defaultOption: "inRange",
        maxNumConditions : 1,
      },
      cellEditor: "numericCellEditor",
      editable: (params:any)=> params.data.Account == 'My Channel' ? true : false,
      onCellValueChanged: (event) =>
      this.gridActionService.UpdateOrderQtyPrice(event,"Price"),
    },
    {
      field: "Status",
      headerTooltip:"Status",
      backendFields:['Status'],
      cellRenderer: "orderStatusCellRendererComponent",
      width: 180,
      sortable:true,
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["Pending", "Unshipped", "Acknowledged","Shipped","Cancelled",'Delivered','Created',"Removal"],
      },
    },
    {
      field: "Channel",
      backendFields:['Channel'],
      headerTooltip:"Channel",
      cellRenderer: "channelsCellRendererComponent",
      cellRendererParams: (params) => {
        return {
          channels: [params.data.Channel],
        };
      },
      width: 120,
      filter: "agTextColumnFilter",
      sortable: true,
    },
    {

      field: "ProductsMapped",
      headerName: "Mapped Products",
      headerTooltip: "Mapped Products",
      backendFields: ["ProductsMapped"],
      width: 120,
      minWidth: 100,
      filter: "agTextColumnFilter",
      filterParams: {
        defaultOption: "contains",
        maxNumConditions : 1,
      },
      sortable:true
    }, 
{
      headerName: "Account",
      field: "Account",
      headerTooltip:"Account",
      backendFields: ['Account'],
      filter: "agTextColumnFilter",
      // filterParams: (param)  =>{
      //   return {
      //     values: [...this.allAccounts],
      //   }
      // }, 
      width: 120,
      sortable:true,
    },

    {
      field: "FulfillmentType",
      backendFields: ["FulfillmentType"],
      headerName: "Fulfillment",
      headerTooltip:"Fulfillment",
      width: 120,
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["FBA", "FBM"],
      },
    },
    {
      field: "poid",
      headerName: "Order number",
      headerTooltip: "Order number",
      backendFields:["poid"],
      filter:'agTextColumnFilter',
      sortable:true
    },
    {
      field: "Isbsns",
      backendFields: ["Isbsns"],
      headerName: "Business",
      sortable:true,
      headerTooltip:"Business",
      cellRenderer: "checkboxCellRendererComponent",
      filter: "booleanFilterComponent",
      width: 120,
    },
    {

      field: "ShipCost",
      headerName: "Ship Cost",
      backendFields: ["ShipCost"],
      headerTooltip:"Ship Cost",
      sortable:true,
      width: 120,
      valueFormatter: (param) =>
        param.value ? "$" + (parseFloat(Number(param.value).toFixed(2))).toLocaleString('en') : "",
      filter: "agNumberColumnFilter",
      filterParams: {
        defaultOption: "inRange",
        maxNumConditions : 1,
      },
     
    },
  ];

  components = {
    skuCellRendererComponent: SkuCellRendererComponent,
    statusBarRendererComponent: StatusBarRendererComponent,
    loadingCellRenderer: LoadingCellRendererComponent,
    orderInfoCellRendererComponent: OrderInfoCellRendererComponent,
    channelsCellRendererComponent: ChannelsCellRendererComponent,
    orderStatusCellRendererComponent: OrderStatusCellRendererComponent,
    checkboxCellRendererComponent: CheckboxCellRendererComponent,
    customHeaderTooltipComponent:CustomHeaderTooltipComponent,
    skuToolPanelComponent: SkuToolPanelComponent,
    multiSkuToolPanelComponent: MultiSkuToolPanelComponent,
    booleanFilterComponent: BooleanFilterComponent,
  };

  gridOptions: GridOptions = {
    rowModelType: "serverSide",
    blockLoadDebounceMillis: 1,
    suppressServerSideInfiniteScroll: false,
    tooltipShowDelay:800,
    suppressCopyRowsToClipboard: true,
    rowSelection: "multiple",
    defaultColDef: {
      resizable: true,
      filterParams: {
        maxNumConditions : 1,
      },
      menuTabs: ["filterMenuTab"],
      tooltipComponent:"customHeaderTooltipComponent",
    },
    components: this.components,
    statusBar: {
      statusPanels: [
        { statusPanel: "statusBarRendererComponent", align: "left" },
      ],
    },
    rowHeight: 58,
    headerHeight: 50,
    suppressRowClickSelection: false,
    loadingCellRenderer: "loadingCellRenderer",
     sideBar: {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
        {
          id: "singleSku",
          labelDefault: "single sku",
          labelKey: "sku",
          iconKey: "sku",
          toolPanel: "skuToolPanelComponent",
        },
        {
          id: "multiSku",
          labelDefault: "multi sku",
          labelKey: "sku",
          iconKey: "sku",
          toolPanel: "multiSkuToolPanelComponent",
        },
      ],
    },
    cacheBlockSize: 100,
    pivotPanelShow: "onlyWhenPivoting",
    rowBuffer: 50,
    enableCellChangeFlash: true,
    suppressMenuHide: true,
    icons: {
      menu: '<span class="ag-icon ag-icon-filter" unselectable="on" role="presentation"></span>',
      filter:
        '<span class="ag-icon ag-icon-filter" style="display:none;" unselectable="on" role="presentation"></span>',
    },
    onGridReady: (e) => {
      this.gridApiService.gridApi$.next(e);
      this.firstRender = true;
      this.gridActionsService.applycolumnState(e);
      this.setDateFilter(this.dateFilter);
    },
    onColumnVisible: (e) => this.gridActionsService.columnVisible(e,this.isGridReady),
    onColumnPinned: () => this.gridActionsService.saveColumnState(null,this.isGridReady),
    onDragStopped: () => this.gridActionsService.saveColumnState(null,this.isGridReady),
    onSortChanged: () => this.gridActionsService.saveColumnState(null,this.isGridReady),
    onFirstDataRendered:() => this.isGridReady = true,
    // onFirstDataRendered: () => this.gridActionsService.applycolumnState(),
    onRowSelected: () => this.gridActionsService.updateSelectedRows(),
    onFilterChanged: () => this.gridActionsService.updateSelectedFilters(null),
    onCellKeyDown: (e) => this.gridActionsService.onCellKeyDown(e),
    onModelUpdated: (e) => {
      if (this.firstRender && e.api.getDisplayedRowCount() > 1) {
       // this.gridApi.sizeColumnsToFit();
        this.firstRender = false;
      }
    },
    onRowClicked: ($event) => {
     if($event.data) {
      this.gridActionsService.updateSelectedRows($event);
        if (this.gridActionService.isOpened) { 
          this.gridActionsService.openSidePanel('singleSku');
          this.refreshSelectedSku();
      }
    }
    },
  };

  setDateFilter(e: any) {
    this.dateFilter = e?.selectedDate ?  e?.selectedDate : e;
    this.dialogRef = e?.dialogRef;
    this.spin = e?.spinner;
    if (this.gridApi) {
      let filterInstance:any = this.gridApi.getFilterInstance("Purchased");
      if(filterInstance){
        filterInstance=filterInstance?.filters[1]
        filterInstance?.setModel({
          filterType: "date",
          type: "inRange",
          dateFrom:  new Date(this.dateFilter.from.getTime() - (this.dateFilter.from.getTimezoneOffset() * 60000)).toISOString(),
          dateTo: new Date(this.dateFilter.to.getTime() - (this.dateFilter.to.getTimezoneOffset() * 60000)).toISOString(),
        });
        this.gridApi.onFilterChanged();
      }
    }
  }

  refreshSelectedSku() {
    const toolpanelComponent = this.gridApi.getToolPanelInstance('singleSku');
    if (toolpanelComponent) {
      toolpanelComponent.refresh(null);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
